import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';

interface Slot {
  id: string;
  [key: string]: any;
}

interface EditFieldDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  slot: Slot;
  fieldName: string;
  fieldValue: string;
  onSubmit: (slotId: string, fieldName: string, fieldValue: string) => void;
  onClose: () => void;
}

const EditFieldDialog: React.FC<EditFieldDialogProps> = ({
  open,
  setOpen,
  slot,
  fieldName,
  fieldValue,
  onSubmit,
  onClose
}) => {
  const [field, setField] = useState("");

  useEffect(() => {
    if (open) {
      setField(fieldValue);
    }
  }, [open, fieldValue]);

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleSubmit = () => {
    if (field?.length > 0) {
      onSubmit(slot.id, fieldName, field);
      setTimeout(() => {
        setField("");
        handleClose();
      }, 300);
    }
  };

  const submitEnabled = field?.length > 0;

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="edit-field-dialog-title">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 id="edit-field-dialog-title" className="text-lg font-semibold text-gray-900">Update {fieldName}</h3>
          <button
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {fieldName}
            </label>
            <textarea
              placeholder={`Add ${fieldName} here....`}
              value={field}
              onChange={(e) => {
                if (e.target.value?.length < 600) {
                  setField(e.target.value);
                }
              }}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
            <div className="text-right text-sm text-gray-500 mt-1">
              {field.length}/600 characters
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={!submitEnabled}
            className={`w-full px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
              submitEnabled
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Save className="h-4 w-4" />
            <span>Submit</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditFieldDialog;
